import React from 'react';

import type { Cart } from '@src/domains/cart';
import {
  SUMMARY_SOURCE,
  canManageCartCoupon,
  isCartSummaryPresentation,
  isDirectLinkPaymentPresentation,
  isOrderSummaryPresentation,
  isSaleChangeSummaryPresentation,
  isSaleSummaryPresentation,
  type SummarySource,
  type Transactable,
} from '@src/domains/transactables';
import { CartSummary } from '@src/features/cart';
import { DirectLinkPaymentSummary } from '@src/features/direct-link/summary';
import { OrderSummary } from '@src/features/orders';
import { SaleChangeSummary } from '@src/features/sale-change/summary';
import { SaleSummary } from '@src/features/sales/summary';

export type TransactableSummaryContentProps = {
  source: SummarySource;
  transactable: Transactable;
  cart?: Cart;
  canManageCoupon?: boolean;
};

export const TransactableSummaryContent = (props: TransactableSummaryContentProps) => {
  const { source, transactable, cart, canManageCoupon: canManageCouponOverride } = props;
  const { order, sale, saleChange, directLink } = transactable;
  const canManageCoupon = canManageCouponOverride ?? canManageCartCoupon(source, transactable);

  if (isSaleChangeSummaryPresentation(source)) {
    return saleChange ? <SaleChangeSummary saleChange={saleChange} sale={sale} order={order} /> : null;
  }

  if (isOrderSummaryPresentation(source)) {
    return order ? <OrderSummary order={order} /> : null;
  }

  if (source === SUMMARY_SOURCE.DIRECT_LINK) {
    if (!directLink) {
      return null;
    }

    if (isCartSummaryPresentation(source, transactable)) {
      return cart ? <CartSummary cart={cart} canManageCoupon={canManageCoupon} /> : null;
    }

    if (isSaleSummaryPresentation(source, transactable)) {
      return sale ? <SaleSummary sale={sale} /> : null;
    }

    if (isDirectLinkPaymentPresentation(source, transactable)) {
      return <DirectLinkPaymentSummary directLink={directLink} />;
    }

    return null;
  }

  if (source === SUMMARY_SOURCE.SALE) {
    return sale ? <SaleSummary sale={sale} /> : null;
  }

  if (source === SUMMARY_SOURCE.CART) {
    return cart ? <CartSummary cart={cart} canManageCoupon={canManageCoupon} /> : null;
  }

  return null;
};

export default TransactableSummaryContent;
